// Swansea job pages. These are about the job, not a copy of the town page.
// Scrap: free to post, a local scrap collector can collect it, no cost to the
// customer.
// Checked 29 October 2026:
//   NRW POPs guidance (sofas) — page title confirmed.
//   Swansea bulky waste, scrap metal dealers, recycling centres and fly-tipping
//   URLs are the council's own paths. A direct fetch got a bot check, so no
//   price was copied off the bulky waste page.
//   Environment Centre student move-out: https://www.environmentcentre.org.uk/moveout

const link = (text, href) => ({ t: text, href });

const NRW_POPS =
  'https://naturalresources.wales/guidance-and-advice/environmental-topics/waste-management/manage-waste-upholstered-domestic-seating-containing-persistent-organic-pollutants-pops/?lang=en';

const SWANSEA_BULKY = 'https://www.swansea.gov.uk/bulkywaste';
const SWANSEA_RECYCLING = 'https://www.swansea.gov.uk/recyclingcentres';
const SWANSEA_SCRAP = 'https://www.swansea.gov.uk/scrapmetaldealers';

const NOT_A_BOOKING =
  'Posting is free. It does not book a van, and it does not mean anyone will send a price.';

export const JOBS = [
  {
    slug: 'house-clearance',
    path: '/swansea/house-clearance',
    serviceType: 'House clearance',
    title: 'House clearance in Swansea, free to post | The WasteGame',
    description:
      'Clearing a house, flat or student let in Swansea? Post the job free, get prices from registered waste carriers, and pay by card only when it is done.',
    h1: 'House clearance in Swansea',
    blocks: [
      {
        type: 'p',
        text: 'A clearance is priced on volume, access and what has to be separated: furniture, white goods, mattresses, garden waste. Say how many rooms, which floor, and what stays behind. The WasteGame does not collect the waste, and it holds no waste carrier licence. It connects you with registered waste carriers.',
      },
      {
        type: 'p',
        text: [
          NOT_A_BOOKING,
          ' If carriers want the work, they send their own prices. You pick one, or you pick none. You pay by card only after the job is marked complete. There is no customer fee. If good furniture is in the house, a reuse charity may take it. That is covered in the ',
          link('free collection part of the scrap guide', '/guides/free-scrap-collection-swansea#reuse'),
          '.',
        ],
      },
      {
        type: 'h2',
        text: 'What to put in your job post',
      },
      {
        type: 'ul',
        items: [
          'How many rooms, and whether it is a flat, a student let or a family house.',
          'Which floor, and whether there is a lift, a stair turn, or a rear lane.',
          'The large items, and what is staying for the next person.',
          'Where a van can stop. A photo of the pile and the stairs helps.',
        ],
      },
      {
        type: 'h2',
        text: 'The legal bit',
      },
      {
        type: 'note',
        text: 'You have a legal duty of care for your household waste. Ask where it is going, and ask for a waste transfer note or a receipt. If waste is later fly-tipped, the householder can be fined. Posting on The WasteGame does not move that duty onto the site.',
      },
    ],
    faqs: [
      {
        q: 'Do I pay a deposit?',
        a: 'No. On a priced job, card payment happens after the job is marked complete. You pay the price you accepted. The WasteGame does not add a customer fee. Posting is free.',
      },
      {
        q: 'Can a landlord or letting agent post for a tenant’s property?',
        a: 'Yes. Post it from your own customer account and say who will be there. Posting still does not book a van, and it does not mean anyone will send a price.',
      },
      {
        q: 'What if no one sends a price?',
        a: [
          'You have not been charged. Try a clearer list or photos, or use Swansea Council’s ',
          link('recycling centres', SWANSEA_RECYCLING),
          ' or the council’s ',
          link('bulky waste collection', SWANSEA_BULKY),
          '. Check the council page for the current bulky collection price.',
        ],
      },
      {
        q: 'Does The WasteGame clear the house itself?',
        a: 'No. The WasteGame does not collect waste and holds no waste carrier licence. Registered waste carriers send a price if they want the job.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/guides/rubbish-removal-cost-swansea', label: 'What changes the price' },
      { href: '/guides/check-a-waste-carrier-licence', label: 'How to check a waste carrier registration' },
      { href: '/swansea/sofa-removal', label: 'Sofa removal' },
      { href: '/swansea/mattress-removal', label: 'Mattress removal' },
      { href: '/swansea/fridge-freezer-removal', label: 'Fridge and freezer removal' },
    ],
  },
  {
    slug: 'free-scrap-collection',
    path: '/swansea/free-scrap-collection',
    serviceType: 'Scrap collection',
    title: 'Free scrap metal and old appliance collection in Swansea | The WasteGame',
    description:
      'Post old washing machines, cookers, bikes or scrap metal in Swansea for free. A local scrap collector can collect it at no cost to you. A time is not promised.',
    h1: 'Free scrap metal and old appliance collection in Swansea',
    blocks: [
      {
        type: 'p',
        text: 'Free to post. A local scrap collector collects it, and there is no cost to the customer. Posting does not book a visit, and it does not promise a price or a time. If nobody takes the job, you have not been charged.',
      },
      {
        type: 'p',
        text: 'The WasteGame does not collect the scrap and holds no waste carrier licence. It connects you with collectors.',
      },
      {
        type: 'p',
        text: [
          'In law, someone collecting scrap metal door to door needs a scrap metal collector’s licence from the council for that area, as well as a waste carrier registration. The licence should be displayed on the vehicle. Swansea Council issues these licences. Read ',
          link('Swansea Council’s scrap metal dealers page', SWANSEA_SCRAP),
          ' and ask to see the licence. That check is yours. Cash payment for scrap metal is not allowed. This page does not promise that anyone will pay you.',
        ],
      },
      {
        type: 'p',
        text: [
          'Do not call a fridge or a freezer scrap. They contain refrigerant gases and are handled as hazardous electrical waste. Say on the post that it is a fridge or freezer, or use the ',
          link('fridge and freezer removal page', '/swansea/fridge-freezer-removal'),
          '.',
        ],
      },
      {
        type: 'h2',
        text: 'What to put in your job post',
      },
      {
        type: 'ul',
        items: [
          'What it is: washing machine, cooker, bike, or loose metal. Give a count.',
          'The floor, the stairs or lift, and where a vehicle can stop.',
          'A photo.',
          'Call out anything that is not scrap: a fridge, a freezer, a gas bottle, a battery, or anything with oil or fuel in it.',
        ],
      },
      {
        type: 'h2',
        text: 'The legal bit',
      },
      {
        type: 'note',
        text: 'Household waste is still your duty of care. Ask where it is going and ask for a waste transfer note or a receipt.',
      },
    ],
    faqs: [
      {
        q: 'Is it really free?',
        a: 'Posting is free, and a scrap collection costs the customer nothing. That is not a promise that a collector will come, or when. If nobody takes it, you pay nothing because you were not charged to post.',
      },
      {
        q: 'Will the collector pay me for the scrap?',
        a: 'Do not count on it. This page does not promise a payment. Cash for scrap metal is not allowed. A time is not promised either.',
      },
      {
        q: 'What cannot go as scrap?',
        a: 'Fridges and freezers, gas bottles, batteries, and anything with oil or fuel in it. Say what the item is. A fridge is hazardous electrical waste, not scrap.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/guides/free-scrap-collection-swansea', label: 'Free scrap guide' },
      { href: '/guides/check-a-waste-carrier-licence', label: 'How to check a waste carrier registration' },
      { href: '/swansea/fridge-freezer-removal', label: 'Fridge and freezer removal' },
    ],
  },
  {
    slug: 'sofa-removal',
    path: '/swansea/sofa-removal',
    serviceType: 'Sofa removal',
    title: 'Sofa removal in Swansea, free to post | The WasteGame',
    description:
      'Need an old sofa or armchair taken away in Swansea? Post it free, registered waste carriers send prices, and you pay by card when it is collected.',
    h1: 'Sofa removal in Swansea',
    blocks: [
      {
        type: 'p',
        text: [
          'In Wales, old upholstered seating such as sofas, armchairs and sofa beds usually contains persistent organic pollutants (POPs) and must not go to landfill. It has to be incinerated. A carrier who takes your sofa should be taking it somewhere that can deal with it. That is ',
          link('Natural Resources Wales guidance on waste upholstered domestic seating', NRW_POPS),
          '. The WasteGame does not collect the sofa and holds no waste carrier licence.',
        ],
      },
      {
        type: 'p',
        text: `${NOT_A_BOOKING} If the sofa is in good condition and still has a fire label, a reuse charity may collect it. You pay by card only if you accept a price and the job is marked complete. There is no customer fee.`,
      },
      {
        type: 'h2',
        text: 'What to put in your job post',
      },
      {
        type: 'ul',
        items: [
          'The size: a 2-seater, a 3-seater, a corner sofa, or an armchair.',
          'The floor, and whether there are stairs or a lift.',
          'Whether it comes apart.',
          'A photo, including the stairwell if it has to come down.',
        ],
      },
      {
        type: 'h2',
        text: 'The legal bit',
      },
      {
        type: 'note',
        text: 'Upholstered seating is your household waste until it is handed to someone who can take it legally. Ask where it is going and ask for a waste transfer note or a receipt. Do not leave it in a lane.',
      },
    ],
    faqs: [
      {
        q: 'Is it free to post a sofa?',
        a: 'Yes. Posting costs nothing and does not book a van. You pay by card only if you accept a price and the job is marked complete. There is no customer fee.',
      },
      {
        q: 'Can the sofa go to landfill?',
        a: [
          'Domestic upholstered seating that contains POPs must not go to landfill. Read the ',
          link('Natural Resources Wales guidance', NRW_POPS),
          ' and ask the carrier where the sofa is going.',
        ],
      },
      {
        q: 'What if the sofa is still decent?',
        a: 'If it is in good condition and has a fire label, ask a reuse charity before you pay for disposal. A broken sofa is a collection, not a donation.',
      },
      {
        q: 'What if nobody sends a price?',
        a: 'You have not been charged. You can try the council’s bulky collection or a recycling centre if you can move it yourself. Check the council page for the current rules and price.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/swansea/house-clearance', label: 'House clearance in Swansea' },
      { href: '/guides/rubbish-removal-cost-swansea', label: 'What changes the price' },
      { href: '/guides/free-scrap-collection-swansea#reuse', label: 'Reuse before you throw it out' },
    ],
  },
  {
    slug: 'mattress-removal',
    path: '/swansea/mattress-removal',
    serviceType: 'Mattress removal',
    title: 'Mattress removal in Swansea, free to post | The WasteGame',
    description:
      'Post a mattress collection in Swansea for free. Registered waste carriers send prices. Pay by card only after it is gone. Useful for end-of-tenancy clear-outs.',
    h1: 'Mattress removal in Swansea',
    blocks: [
      {
        type: 'p',
        text: 'Say whether it is a single, double or king, how many there are, and whether it is wet or soiled. A dry mattress is easier to recycle than a wet one. End-of-tenancy clear-outs are common around Uplands and Brynmill. Name the neighbourhood and the floor.',
      },
      {
        type: 'p',
        text: [
          NOT_A_BOOKING,
          ' You pay by card only after the job is marked complete. There is no customer fee. If you have a car, Swansea’s recycling centres are the other legal option. Check ',
          link('the council’s recycling centres page', SWANSEA_RECYCLING),
          ' for what each site takes. Not every mattress type is accepted the same way.',
        ],
      },
      {
        type: 'h2',
        text: 'What to put in your job post',
      },
      {
        type: 'ul',
        items: [
          'Size and how many mattresses.',
          'Dry, stained, or wet. Say so. Do not leave it as a surprise.',
          'The floor, stairs, and whether it is already outside.',
          'A photo.',
        ],
      },
      {
        type: 'h2',
        text: 'The legal bit',
      },
      {
        type: 'note',
        text: 'A mattress left in a lane is fly-tipping. It is your duty of care until a carrier takes it. Ask for a waste transfer note or a receipt. The WasteGame does not collect it and holds no waste carrier licence.',
      },
    ],
    faqs: [
      {
        q: 'Is posting a mattress job free?',
        a: 'Yes. You pay by card only if you accept a price and the job is marked complete. There is no customer fee. Posting does not book a van.',
      },
      {
        q: 'I am leaving a student house in Brynmill. Is this the right page?',
        a: 'Yes, if the mattress is in Swansea, including Uplands and Brynmill. Say how many, the size, and the floor. A carrier may still choose not to send a price.',
      },
      {
        q: 'Can I take it to the tip?',
        a: [
          'Often, if you can transport it and the site accepts that type of mattress. Start with ',
          link('Swansea’s recycling centres page', SWANSEA_RECYCLING),
          '.',
        ],
      },
      {
        q: 'What if it is wet?',
        a: 'Say so on the post. A wet mattress is heavier and some sites will not take it. A price, if you get one, is for the mattress you described.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/swansea/house-clearance', label: 'House clearance in Swansea' },
      { href: '/guides/rubbish-removal-cost-swansea', label: 'What changes the price' },
    ],
  },
  {
    slug: 'fridge-freezer-removal',
    path: '/swansea/fridge-freezer-removal',
    serviceType: 'Fridge and freezer removal',
    title: 'Fridge and freezer removal in Swansea | The WasteGame',
    description:
      'Old fridge or freezer in Swansea? Post it free, registered waste carriers send prices, and you pay by card when it is collected. Fridges need proper disposal.',
    h1: 'Fridge and freezer removal in Swansea',
    blocks: [
      {
        type: 'p',
        text: 'Fridges and freezers hold refrigerant gases and foam that must be dealt with properly. They are classed as hazardous waste electrical equipment, so they must not be dumped or broken up for scrap. Say on the post that it is a fridge or a freezer. Do not list it as scrap metal.',
      },
      {
        type: 'p',
        text: [
          NOT_A_BOOKING,
          ' You pay by card only after the job is marked complete. There is no customer fee. Swansea Council also runs a paid ',
          link('bulky waste collection', SWANSEA_BULKY),
          ' that takes fridges and freezers. Check that page for the current price and for what it will not collect. Retailers often take an old one back when they deliver a new one, so it is worth asking.',
        ],
      },
      {
        type: 'h2',
        text: 'What to put in your job post',
      },
      {
        type: 'ul',
        items: [
          'Fridge, freezer, or fridge-freezer, and how many.',
          'Whether it is empty, and the floor it is on.',
          'Stairs, a lift, or a step up to the kitchen.',
          'A photo. Mention if the door is off or the cabinet is damaged.',
        ],
      },
      {
        type: 'h2',
        text: 'The legal bit',
      },
      {
        type: 'note',
        text: 'A fridge is not scrap. Breaking it up to get at the metal releases the gases. Ask the carrier where it is going and ask for a waste transfer note or a receipt. The WasteGame does not collect it and holds no waste carrier licence.',
      },
    ],
    faqs: [
      {
        q: 'Is a fridge the same as scrap collection?',
        a: 'No. Scrap collection on The WasteGame has no cost to the customer, but a fridge or freezer must not be posted as scrap. Post it here so the carrier knows it needs proper disposal. A price, if you get one, is paid by card after the job is marked complete.',
      },
      {
        q: 'Does the council take fridges?',
        a: [
          'Swansea Council’s ',
          link('bulky waste collection', SWANSEA_BULKY),
          ' lists fridges and freezers. The price is on that page. Check it, because it can change. You can also ask the retailer who delivers a new appliance.',
        ],
      },
      {
        q: 'Do I pay a deposit?',
        a: 'No. Posting is free and does not book a van. Card payment is only for a price you accepted, after the job is marked complete.',
      },
      {
        q: 'What if nobody sends a price?',
        a: 'You have not been charged. Use the council collection or a retailer take-back if those fit. Do not leave the appliance in the lane.',
      },
    ],
    links: [
      { href: '/areas/swansea', label: 'Rubbish removal in Swansea' },
      { href: '/swansea/free-scrap-collection', label: 'Scrap collection, for metal that is not a fridge' },
      { href: '/guides/free-scrap-collection-swansea', label: 'What never counts as scrap' },
      { href: '/guides/check-a-waste-carrier-licence', label: 'How to check a waste carrier registration' },
    ],
  },
];

export function jobBySlug(slug) {
  return JOBS.find((job) => job.slug === slug) || null;
}

export function jobLinks() {
  return JOBS.map((job) => ({ href: job.path, label: job.h1 }));
}
